"use client"

import { useEffect, useCallback, useMemo, useRef } from "react"
import { getSession, signIn, signOut, useSession } from "next-auth/react"
import { buildAuthUser } from "@/lib/auth/build-auth-user"
import type { UserAuthData } from "@/lib/auth/permissions.types"
import { getQueryClient } from "@/lib/react-query"

// Retained only to remove credentials left by the coordinated cutover.
export const STORAGE_KEY_TOKEN = "gt:access_token"
export const STORAGE_KEY_REFRESH = "gt:refresh_token"

export interface AuthUser {
  user_id: string
  uuid?: string
  tenant_id: string
  email: string
  name?: string
  role?: 'super_user' | 'integrator' | 'admin' | 'manager' | 'viewer'
  permissions?: string[]
  disabled_permissions?: string[]
  impersonated_tenant_id?: string
  timezone: string
  uses_dst: boolean
  /** Display-only; the server session resolver enforces eligibility. */
  email_verified?: boolean
  /**
   * Whether the user's HOME tenant is the platform tenant (derived server-side
   * in the server session DTO). UI hint for gating platform-only surfaces; the server
   * re-checks via isPlatformTenant().
   */
  is_platform_tenant?: boolean
  isManager: boolean
  isAdmin: boolean
  isSuperUser: boolean
  isIntegrator: boolean
  [key: string]: unknown
}

export function useAuth() {
  const { data, status, update } = useSession()
  // Every session poll yields a new session object and a rebuilt update(); load effects
  // across the app key on `user` and `fetchWithAuth`, so both change only with the content.
  const updateRef = useRef(update)
  useEffect(() => { updateRef.current = update }, [update])
  const userKey = data?.user ? JSON.stringify(data.user) : ""
  const user = useMemo(() => (userKey ? buildAuthUser(JSON.parse(userKey) as UserAuthData) : null), [userKey])
  const hasUser = user !== null

  useEffect(() => {
    try {
      for (const key of [STORAGE_KEY_TOKEN, STORAGE_KEY_REFRESH, "globaltalk_token", "globaltalk_refresh_token", "globaltalk_user"]) {
        localStorage.removeItem(key)
      }
    } catch { /* Cookie authentication also works when browser storage is disabled. */ }
  }, [])

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    try {
      const result = await signIn("password", { email, password, rememberMe, redirect: false })
      if (!result?.ok || result.error) return { ok: false, code: result?.error ?? undefined }
      await updateRef.current()
      return { ok: true }
    } catch { return { ok: false } }
  }, [])

  const logout = useCallback(async () => {
    // Await Redis revocation and the cookie-clearing response before showing another account.
    const response = await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" })
    if (!response.ok) throw new Error("Unable to sign out. Please retry.")
    try {
      await signOut({ redirect: false })
    } catch {
      // Authority and cookies are already revoked. Failed library cleanup must not
      // leave a cached authenticated UI; reload to discard it and confirm signout.
      await getQueryClient().cancelQueries()
      getQueryClient().clear()
      window.location.reload()
      return
    }
    await getQueryClient().cancelQueries()
    getQueryClient().clear()
    await updateRef.current()
  }, [])

  const revokeSession = useCallback(async () => {
    const response = await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" })
    if (!response.ok) throw new Error("Unable to revoke session. Please retry.")
  }, [])

  const refreshAuth = useCallback(async () => {
    if (hasUser) {
      await updateRef.current()
      return
    }
    // NextAuth v4 update() intentionally no-ops when its cached session is null.
    if ((await getSession({ broadcast: false }))?.user) window.location.reload()
  }, [hasUser])

  const fetchWithAuth = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    const target = new URL(url, window.location.origin)
    if (target.origin !== window.location.origin) throw new Error("Authenticated requests must be same-origin")
    const headers = new Headers(options.headers)
    headers.delete("Authorization")
    const response = await fetch(url, { ...options, headers, credentials: "same-origin" })
    if (response.status === 401) await updateRef.current()
    return response
  }, [])

  return { user, token: null, isAuthenticated: !!user, isLoading: status === "loading", login, logout, revokeSession, refreshAuth, fetchWithAuth }
}

export async function completeAuthSession(proof: string): Promise<void> {
  // Reuse the browser-bound proof after a lost callback response; never repeat provisioning.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const result = await signIn("completion", { proof, redirect: false })
      if (result?.ok && !result.error) return
      if (result && result.status < 500) break
    } catch {
      // A transport failure may have happened after Redis consumed the proof.
    }
  }
  throw new Error("Unable to complete sign-in. Please retry or sign in.")
}
